
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { auth, db, functions } from '../services/firebase';
import { onAuthStateChanged, signInWithPopup, signOut, GoogleAuthProvider, reauthenticateWithPopup, type User as FirebaseUser } from "firebase/auth";
import { collection, query, where, getDocs, onSnapshot, doc, addDoc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { User, Subscription, SubscriptionPlan, StripeProduct, StripePrice } from '../types';


// Admin email address
const ADMIN_EMAILS = ['dr.svafaie@nvpharmaceutics.com'];

interface AuthContextType {
  user: User | null;
  uid: string | null;
  isAdmin: boolean;
  subscription: Subscription | null;
  loading: boolean;
  shouldShowPricing: boolean;
  products: StripeProduct[];
  productsLoading: boolean;
  isAlertFallbackActive: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  decrementInteractions: () => void;
  isUpgradeModalOpen: boolean;
  setIsUpgradeModalOpen: (isOpen: boolean) => void;
  reauthenticateAdmin: (onSuccess: () => void) => Promise<void>;
  redirectToCheckout: (priceId: string) => Promise<void>;
  redirectToCustomerPortal: () => Promise<void>;
}

// Safe default context value to prevent crashes during HMR, fast refresh, or early render cycles
const defaultAuthValue: AuthContextType = {
  user: null,
  uid: null,
  isAdmin: false,
  subscription: null,
  loading: true,
  shouldShowPricing: false,
  products: [],
  productsLoading: false,
  isAlertFallbackActive: false,
  loginWithGoogle: async () => {},
  logout: async () => {},
  decrementInteractions: () => {},
  isUpgradeModalOpen: false,
  setIsUpgradeModalOpen: () => {},
  reauthenticateAdmin: async () => {},
  redirectToCheckout: async () => {},
  redirectToCustomerPortal: async () => {},
};

// Preserve AuthContext instance across Vite HMR / React Fast Refresh reloads
const globalAuthContext: React.Context<AuthContextType | undefined> = (typeof window !== 'undefined' && (window as any).__DRRX_AUTH_CONTEXT__)
  ? ((window as any).__DRRX_AUTH_CONTEXT__ as React.Context<AuthContextType | undefined>)
  : createContext<AuthContextType | undefined>(undefined);

if (typeof window !== 'undefined') {
  (window as any).__DRRX_AUTH_CONTEXT__ = globalAuthContext;
}

const AuthContext: React.Context<AuthContextType | undefined> = globalAuthContext;

const getFreeTrialSubscriptionDetails = (uid: string): Subscription => {
  const today = new Date().toDateString();
  const lastUsageDay = localStorage.getItem(`drrx_lastUsageDay_${uid}`);
  const interactionsLeftStr = localStorage.getItem(`drrx_interactionsLeft_free_${uid}`);
  
  let interactionsLeft = 10;
  if(lastUsageDay === today && interactionsLeftStr !== null) {
      interactionsLeft = parseInt(interactionsLeftStr, 10);
  }
  return { plan: 'Free', interactionsLeft, interactionLimit: 10 };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [shouldShowPricing, setShouldShowPricing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAlertFallbackActive, setIsAlertFallbackActive] = useState(false);


  // Add a listener for the global alerts configuration
  useEffect(() => {
    const configRef = doc(db, 'system_config', 'alerts_config');
    const unsubscribe = onSnapshot(configRef, (docSnap) => {
        if (docSnap.exists() && docSnap.data().isFrontendFallbackActive === true) {
            setIsAlertFallbackActive(true);
        } else {
            setIsAlertFallbackActive(false);
        }
    }, (error) => {
        // Silent fail to backend default if config read fails (e.g. offline)
        setIsAlertFallbackActive(false); 
    });

    return () => unsubscribe();
  }, []);


  // Fetch products and prices from Firestore
  useEffect(() => {
    // We can only fetch products if a user is logged in, due to security rules.
    if (!firebaseUser) {
        setProductsLoading(false); // Not loading if no user
        setProducts([]); // Clear products on logout
        return;
    }

    const fetchProducts = async () => {
      setProductsLoading(true);

      // Define a base structure for all plans with HARDCODED PRICE IDs
      // This ensures the app works even if the Firestore 'products' collection is empty or syncing fails.
      // Updated with correct pricing: 9.99/29.99 monthly and ~20% discount yearly
      const baseProducts: StripeProduct[] = [
          {
              id: 'prod_free', name: 'Basic', description: 'For casual use and trying out the core features.',
              prices: [], // Basic is free, no Stripe price needed for checkout
              role: 'Free',
              features: [ '10 AI Interactions per Day', 'Standard Clinical Chat', 'Full Drug Index Access', 'National Alerts (FDA, CDC, DEA)', 'Basic Clinical Tools' ],
              active: true,
          },
          {
              id: 'prod_plus', name: 'Plus', description: 'For power users and those needing advanced tools.',
              // Hardcoded Live Price IDs for Plus
              // 9.99 Monthly | Yearly ~20% off = ~7.99/mo = 95.88/yr
              prices: [
                  { id: 'price_1SQyFjLspvUYszJKqm85YvVe', unit_amount: 999, currency: 'usd', interval: 'month', description: null },
                  { id: 'price_1SZI5aLspvUYszJKccPA3B8C', unit_amount: 9588, currency: 'usd', interval: 'year', description: null } 
              ], 
              role: 'Plus',
              features: [ 'Unlimited AI Interactions', 'Advanced Clinical Tools', 'Study Mode (Top 200 Drugs)', 'Clinical Guideline Summaries' ],
              popular: true, active: true,
          },
          {
              id: 'prod_pro', name: 'Pro', description: 'For clinical leaders and researchers needing the ultimate toolkit.',
              // Hardcoded Live Price IDs for Pro
              // 29.99 Monthly | Yearly ~20% off = ~23.99/mo = 287.88/yr
              prices: [
                  { id: 'price_1SQyGjLspvUYszJKpeqlvd7G', unit_amount: 2999, currency: 'usd', interval: 'month', description: null },
                  { id: 'price_1SZI6gLspvUYszJKokZj1bap', unit_amount: 28788, currency: 'usd', interval: 'year', description: null }
              ], 
              role: 'Pro',
              features: [ 'Everything in Plus', 'AI-Powered Evidence Search', 'Advanced Analytics', 'Priority Support' ],
              active: true,
          },
          {
              id: 'prod_teams', name: 'Teams', description: 'For large clinics, institutions, and academic programs.',
              prices: [], // Contact sales
              role: 'Teams',
              features: [ 'Everything in Pro', 'Centralized billing & user management', 'Dedicated account manager', 'Custom onboarding & training', 'API access for integration (optional)', 'Advanced analytics & reporting' ],
              active: true,
          },
      ];
      
      try {
        const q = query(collection(db, 'products'));
        const querySnapshot = await getDocs(q);
        
        const fetchedProducts = new Map<string, StripeProduct>();
        for (const prodDoc of querySnapshot.docs) {
            const productData = prodDoc.data();
            if (productData.role) { // Ensure role exists to use as a key
                const pricesSnap = await getDocs(collection(prodDoc.ref, 'prices'));
                const prices: StripePrice[] = pricesSnap.docs.map(priceDoc => ({ id: priceDoc.id, ...priceDoc.data() } as StripePrice));
                fetchedProducts.set(productData.role, { id: prodDoc.id, ...productData, prices } as StripeProduct);
            }
        }

        // Merge fetched data with base data. 
        const mergedProducts = baseProducts.map(basePlan => {
            const fetchedPlan = fetchedProducts.get(basePlan.role);
            
            if (fetchedPlan) {
                 return {
                     ...basePlan, 
                     ...fetchedPlan,
                     // FORCE base prices to ensure the ~20% yearly discount is always calculable and displayed correctly.
                     // DB prices might be out of sync or lack interval data.
                     prices: basePlan.prices 
                 };
            }
            // If not found in DB, return the base plan with hardcoded prices (Fail-safe)
            return basePlan;
        });
        
        // Sort based on a predefined order
        const planOrder = ['Free', 'Plus', 'Pro', 'Teams'];
        mergedProducts.sort((a, b) => planOrder.indexOf(a.role) - planOrder.indexOf(b.role));

        setProducts(mergedProducts);

      } catch (error) {
        console.error("Error fetching Stripe products from Firestore, using fallback data:", error);
        // On error, just use the hardcoded base products which contain the correct price IDs
        setProducts(baseProducts);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [firebaseUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
      setLoading(true);
      if (fbUser?.isAnonymous) {
        // Discontinue anonymous guest sessions: enforce Google login
        await signOut(auth);
        setFirebaseUser(null);
        setUser(null);
        setSubscription(null);
        setLoading(false);
        return;
      }
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          // Ensure a customer document exists in Firestore for the user.
          const userDocRef = doc(db, 'customers', fbUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (!userDocSnap.exists()) {
            await setDoc(userDocRef, {
              email: fbUser.email || null,
              name: fbUser.displayName || 'Clinical Professional',
              createdAt: new Date().toISOString(),
            });
          }

          const appUser: User = {
            name: fbUser.displayName || 'Clinical Professional',
            email: fbUser.email || undefined,
          };
          setUser(appUser);
          
          // Admin user check
          if (fbUser.email && ADMIN_EMAILS.includes(fbUser.email)) {
              setIsAdmin(true);
              setSubscription({
                  plan: 'Pro',
                  interactionsLeft: Infinity,
                  interactionLimit: 'unlimited',
                  status: 'active',
                  role: 'admin'
              });
              setShouldShowPricing(false);
              setLoading(false);
              return; // Skip normal subscription logic for admin
          }
          
          setIsAdmin(false);

          // Listen for subscription changes for regular users
          const subsRef = collection(db, 'customers', fbUser.uid, 'subscriptions');
          const unsubscribeFromSubs = onSnapshot(subsRef,
              async (snapshot) => { 
                  if (snapshot.empty) {
                      const localSub = getFreeTrialSubscriptionDetails(fbUser.uid);
                      setSubscription(localSub);
                      setShouldShowPricing(localSub.interactionsLeft <= 0);
                      setLoading(false);
                      return;
                  }

                  const activeSubDoc = snapshot.docs.find(doc => ['active', 'trialing'].includes(doc.data().status));
                  
                  if (activeSubDoc) {
                      const subData = activeSubDoc.data() as any;
                      let plan: SubscriptionPlan = (subData.role as SubscriptionPlan) || 'Free';

                      // Fallback: if role missing on sub, check product doc
                      if ((!plan || plan === 'Free') && subData.price) {
                          try {
                              const priceRef = subData.price;
                              const productRef = priceRef.parent.parent;
                              if (productRef) {
                                  const productSnap = await getDoc(productRef);
                                  const productData = productSnap.data() as any;
                                  if (productSnap.exists() && productData.role) {
                                      plan = productData.role as SubscriptionPlan;
                                  }
                              }
                          } catch (e) {
                              console.error("Failed to fetch subscription plan from product as a fallback:", e);
                          }
                      }

                      if (plan !== 'Free' && plan !== 'Trial') {
                          setSubscription({
                              plan,
                              interactionsLeft: Infinity,
                              interactionLimit: 'unlimited',
                              stripeId: activeSubDoc.id,
                              status: subData.status,
                              role: plan,
                          });
                          setShouldShowPricing(false);
                      } else {
                          const localSub = getFreeTrialSubscriptionDetails(fbUser.uid);
                          setSubscription(localSub);
                          setShouldShowPricing(localSub.interactionsLeft <= 0);
                      }
                  } else {
                       const localSub = getFreeTrialSubscriptionDetails(fbUser.uid);
                       setSubscription(localSub);
                       setShouldShowPricing(localSub.interactionsLeft <= 0);
                  }
                  setLoading(false);
              },
              (error) => {
                  console.error("Subscription snapshot error:", error);
                  const localSub = getFreeTrialSubscriptionDetails(fbUser.uid);
                  setSubscription(localSub);
                  setShouldShowPricing(localSub.interactionsLeft <= 0);
                  setLoading(false);
              }
          );
          return () => unsubscribeFromSubs();
        } catch (error) {
            console.error("Critical error during user/subscription initialization.", error);
            const appUser: User = {
              name: fbUser.displayName || 'Clinical Professional',
              email: fbUser.email || undefined,
            };
            setUser(appUser);
            setIsAdmin(false);
            const localSub = getFreeTrialSubscriptionDetails(fbUser.uid);
            setSubscription(localSub);
            setShouldShowPricing(localSub.interactionsLeft <= 0);
            setLoading(false);
        }
      } else {
        setUser(null);
        setSubscription(null);
        setShouldShowPricing(false);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setIsUpgradeModalOpen(false);
      setShouldShowPricing(false);
      // Force redirect to root path to clear query params (e.g. ?view=account) and ensure clean state
      window.location.href = '/'; 
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const reauthenticateAdmin = async (onSuccess: () => void) => {
    if (!firebaseUser || !isAdmin) {
      alert("Access denied.");
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      await reauthenticateWithPopup(firebaseUser, provider);
      onSuccess();
    } catch (error) {
      console.error("Admin re-authentication failed:", error);
      alert("Re-authentication failed. Please try again.");
    }
  };
  
  const redirectToCheckout = async (priceId: string) => {
      if (!firebaseUser) return;
      
      if (!priceId || priceId.includes('PLACEHOLDER')) {
          alert("Configuration Error: Price ID not found for this cycle. Please contact support.");
          console.error("redirectToCheckout called with invalid priceId:", priceId);
          return;
      }

      try {
          const checkoutSessionRef = collection(db, 'customers', firebaseUser.uid, 'checkout_sessions');
          const docRef = await addDoc(checkoutSessionRef, {
              price: priceId,
              success_url: window.location.origin,
              cancel_url: window.location.origin,
          });

          onSnapshot(docRef, (snap) => {
              const { error, url } = snap.data() as { error?: { message: string }, url?: string };
              if (error) {
                  alert(`An error occurred: ${error.message}`);
              }
              if (url) {
                  window.location.assign(url);
              }
          });
      } catch (e) {
          console.error("Checkout redirection failed:", e);
          alert("Failed to start checkout. Please ensure you are connected to the internet.");
      }
  };

  const redirectToCustomerPortal = async () => {
      const functionRef = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      try {
          const { data } = await functionRef({ returnUrl: window.location.origin });
          window.location.assign((data as any).url);
      } catch (error) {
          console.error("Error creating customer portal link:", error);
          alert('Could not open subscription management. Please try again later.');
      }
  };

  const decrementInteractions = () => {
    if (!subscription || !firebaseUser || subscription.interactionLimit === 'unlimited') return;

    if (subscription.interactionsLeft > 0) {
        const newCount = subscription.interactionsLeft - 1;
        setSubscription(prev => ({...prev!, interactionsLeft: newCount }));
        
        if (subscription.plan === 'Free') {
            localStorage.setItem(`drrx_interactionsLeft_free_${firebaseUser.uid}`, String(newCount));
            localStorage.setItem(`drrx_lastUsageDay_${firebaseUser.uid}`, new Date().toDateString());
        } else if (subscription.plan === 'Trial') {
            localStorage.setItem(`drrx_interactionsLeft_trial_${firebaseUser.uid}`, String(newCount));
        }
        
        if (newCount <= 0) {
            setShouldShowPricing(true);
        }
    }
  };

  const value = { 
      user, 
      uid: firebaseUser?.uid || null,
      isAdmin,
      subscription, 
      loading, 
      shouldShowPricing,
      products,
      productsLoading,
      isAlertFallbackActive,
      loginWithGoogle, 
      logout, 
      decrementInteractions, 
      isUpgradeModalOpen, 
      setIsUpgradeModalOpen,
      reauthenticateAdmin,
      redirectToCheckout,
      redirectToCustomerPortal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.warn('[Dr.Rx Auth] useAuth accessed outside AuthProvider or during HMR reload; falling back to safe default state.');
    return defaultAuthValue;
  }
  return context;
};
